import type { GpuSnapshot, GpuDeviceView, GpuProcessView } from '../state/metrics';

/** The llama.cpp server binary's process name (from /proc/<pid>/comm). Used as a
 *  fallback match only — some Ollama versions also name their runner `llama-server`, so
 *  when the agent supplies a server PID we match by PID instead (see below). */
const LLAMACPP_PROC = 'llama-server';

/**
 * Sum the VRAM (MB) of the monitored llama.cpp server's GPU processes. When
 * `serverPid` is known (agent resolved the listening PID) we match that exact PID
 * — excluding Ollama's identically-named runner. Otherwise we fall back to the
 * process name. Dedup by pid (a process appears in both NVML compute+graphics
 * lists). Returns null when no matching process with a known VRAM figure exists.
 */
export function sumLlamacppVramMb(
  processes: GpuProcessView[],
  serverPid?: number | null,
): number | null {
  const seen = new Set<number>();
  let total = 0;
  let matched = false;
  for (const p of processes) {
    const isMatch = serverPid != null ? p.pid === serverPid : p.name === LLAMACPP_PROC;
    if (!isMatch || seen.has(p.pid)) continue;
    seen.add(p.pid); // dedup regardless of whether VRAM is known
    if (p.usedVramMb == null) continue;
    total += p.usedVramMb;
    matched = true;
  }
  return matched ? total : null;
}

/** llama.cpp VRAM across all NVIDIA devices (per-process VRAM is NVIDIA-only). */
export function llamacppVramMb(gpu: GpuSnapshot | null, serverPid?: number | null): number | null {
  if (!gpu) return null;
  const procs = gpu.devices
    .filter((d) => d.vendor === 'nvidia')
    .flatMap((d) => d.processes);
  return sumLlamacppVramMb(procs, serverPid);
}

/** llama.cpp VRAM on one device, as a number (0 when none) for summing into a segment. */
export function llamacppVramForDevice(
  device: GpuDeviceView | null,
  serverPid?: number | null,
): number {
  if (!device || device.vendor !== 'nvidia') return 0;
  return sumLlamacppVramMb(device.processes, serverPid) ?? 0;
}

export type Placement = 'gpu' | 'partial' | 'vram-only' | 'none';

/**
 * Placement flag from measured VRAM vs the model's on-disk size. Process VRAM
 * includes KV-cache/buffers, so a fully-offloaded model reads VRAM >= size; a
 * spilled one reads VRAM < size. Heuristic — surfaced with an ⓘ caveat.
 *
 * While the server reports the model as sleeping (unloaded), any residual VRAM is
 * buffers, not weights — comparing it to the file size would claim "partly on CPU"
 * about a model that isn't loaded at all, so the verdict is withheld.
 */
export function placement(
  vramMb: number | null,
  sizeMb: number | null,
  isSleeping?: boolean | null,
): Placement {
  if (vramMb == null) return 'none';
  if (isSleeping === true) return 'vram-only';
  if (sizeMb == null) return 'vram-only';
  return vramMb >= sizeMb ? 'gpu' : 'partial';
}
